import type { TimelineSnapshot } from "@/types/burndown.types";

export interface BreakEvenPlan {
  operatingBreakEvenRevenue: number;
  currentMonthlyRevenue: number;
  monthlyRevenueGap: number;
  isOperatingBreakEven: boolean;
  monthlyBurn: number;
  cashBalance: number;
  runwayMonths: number;
  /** Months before cash runs out at current net burn (finite runway only). */
  monthsBeforeCashZero: number | null;
  /** Avg monthly revenue increase needed to reach break-even before cash runs out. */
  requiredMonthlyRevenueRamp: number | null;
}

export function calculateBreakEvenPlan(
  snapshot: Pick<
    TimelineSnapshot,
    | "monthlyBurn"
    | "monthlyRevenue"
    | "netMonthlyChange"
    | "cashBalance"
    | "runway"
  > | null | undefined,
): BreakEvenPlan | null {
  if (!snapshot) {
    return null;
  }

  const monthlyBurn = Math.max(0, snapshot.monthlyBurn);
  const currentMonthlyRevenue = Math.max(0, snapshot.monthlyRevenue);
  const monthlyRevenueGap = Math.max(0, monthlyBurn - currentMonthlyRevenue);
  const isOperatingBreakEven = monthlyRevenueGap <= 0;
  const runwayMonths =
    snapshot.runway === Infinity ? Infinity : Math.max(0, snapshot.runway);

  let monthsBeforeCashZero: number | null = null;
  let requiredMonthlyRevenueRamp: number | null = null;

  if (!isOperatingBreakEven && snapshot.netMonthlyChange < 0) {
    if (runwayMonths !== Infinity) {
      monthsBeforeCashZero = Math.floor(runwayMonths);
      const months = Math.max(1, monthsBeforeCashZero);
      requiredMonthlyRevenueRamp = monthlyRevenueGap / months;
    }
  }

  return {
    operatingBreakEvenRevenue: monthlyBurn,
    currentMonthlyRevenue,
    monthlyRevenueGap,
    isOperatingBreakEven,
    monthlyBurn,
    cashBalance: snapshot.cashBalance,
    runwayMonths,
    monthsBeforeCashZero,
    requiredMonthlyRevenueRamp,
  };
}
